import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, isDevMode } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  EnhancementVariantCreate,
  EnhancementVariantPaging,
  IMasterSuite,
  MasterSuiteEnhancementVariantAssetsFile,
  IMasterSuiteTouch,
  IMasterSuiteTouchAsset,
  ISettingsVariant,
} from '../models/mastersuites/mastersuite';
import {
  EnhancementVariantListResult,
  EnhancementVariantResult,
  MasterSuiteDeleteResult,
  MasterSuiteListResult,
  MasterSuiteResult,
  MasterSuiteUpdateResult,
  SettingsVariantDeleteResult,
  SettingsVariantListResult,
  SettingsVariantResult,
} from '../models/mastersuites/mastersuite-result';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class MasterSuitesService {
  private masterSuite$ = new BehaviorSubject<IMasterSuite | any>({});
  public currentMasterSuite = this.masterSuite$.asObservable();
  constructor(private http: HttpClient) {}

  updateMasterSuiteState(masterSuite: IMasterSuite) {
    this.masterSuite$.next(masterSuite);
  }

  listMasterSuites(orgId: string | null): Observable<IMasterSuite[]> {
    const url = `${environment.apiBase}/master-suites?organizationId=${orgId}`;
    if (isDevMode()) {
      console.log('[DEV] [MasterSuitesService] loading master suites');
    }
    return this.http.get<MasterSuiteListResult>(url, httpOptions).pipe(map((res: MasterSuiteListResult) => res.masterSuites));
  }

  createMasterSuite(newMasterSuite: IMasterSuite): Observable<MasterSuiteResult> {
    const url = `${environment.apiBase}/master-suites`;
    if (isDevMode()) {
      console.log('[DEV] [MasterSuitesService] creating master suite', newMasterSuite);
    }
    return this.http.post<MasterSuiteResult>(url, newMasterSuite, httpOptions);
  }

  updateMasterSuite(masterSuite: IMasterSuite): Observable<MasterSuiteUpdateResult> {
    const url = `${environment.apiBase}/master-suites/${masterSuite.id}`;
    if (isDevMode()) {
      console.log('[DEV] [MasterSuitesService] updating master suite', masterSuite.id);
    }
    return this.http.put<MasterSuiteUpdateResult>(url, masterSuite, httpOptions);
  }

  deleteMasterSuite(masterSuite: IMasterSuite): Observable<MasterSuiteDeleteResult> {
    if (isDevMode()) {
      console.log('[DEV] [MasterSuitesService] deleting master suite', masterSuite.id);
    }
    const url = `${environment.apiBase}/master-suites/${masterSuite.id}`;
    return this.http.delete<MasterSuiteDeleteResult>(url, httpOptions);
  }

  detailMasterSuite(masterSuiteId: string): Observable<IMasterSuite> {
    const url = `${environment.apiBase}/master-suites/${masterSuiteId}`;
    if (isDevMode()) {
      console.log('[DEV] [MasterSuitesService] loading master suite details', masterSuiteId);
    }
    return this.http.get<MasterSuiteResult>(url, httpOptions).pipe(map((res: MasterSuiteResult) => res.masterSuite));
  }

  /*
  ACTIONS
  */

  masterSuiteTouchAssetEnhancementPlaceholderUpload(
    masterSuiteEnhancementVariantAssetId: string,
    file: File,
  ) {
    const url = `${environment.apiBase}/enhancement-variant-assets/${masterSuiteEnhancementVariantAssetId}/placeholder`;
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<MasterSuiteEnhancementVariantAssetsFile>(url, formData);
  }
  masterSuiteTouchAssetShootPlaceholderUpload(masterSuiteTouchAssetId: string, file: File) {
    const url = `${environment.apiBase}/master-suite-touch-asset/${masterSuiteTouchAssetId}/shoot-placeholder`;
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<IMasterSuiteTouchAsset>(url, formData);
  }

  updateMasterSuiteTouchNotes(id: string, notes: string) {
    const url = `${environment.apiBase}/master-suite-touch/${id}/notes`;
    return this.http.put<IMasterSuiteTouch>(
      url,
      {
        notes: notes,
      },
      httpOptions,
    );
  }

  // Settings Variants
  listSettingsVariants(masterSuiteId: string): Observable<SettingsVariantListResult> {
    const url = `${environment.apiBase}/master-suite/${masterSuiteId}/settings-variants`;
    if (isDevMode()) {
      console.log('[DEV] [MasterSuitesService] loading settings variants', masterSuiteId);
    }
    return this.http.get<SettingsVariantListResult>(url, httpOptions);
  }

  createSettingsVariant(settingsVariant: ISettingsVariant): Observable<SettingsVariantResult> {
    const url = `${environment.apiBase}/master-suite/${settingsVariant.masterSuiteId}/settings-variant`;
    if (isDevMode()) {
      console.log('[DEV] [MasterSuitesService] creating settings variant', settingsVariant);
    }
    return this.http.post<SettingsVariantResult>(url, settingsVariant, httpOptions);
  }

  updateSettingsVariant(settingsVariant: ISettingsVariant): Observable<ISettingsVariant> {
    const url = `${environment.apiBase}/settings-variant/${settingsVariant.id}`;
    if (isDevMode()) {
      console.log('[DEV] [MasterSuitesService] updating settings variant', settingsVariant.id);
    }
    return this.http.put<ISettingsVariant>(url, settingsVariant, httpOptions);
  }

  deleteSettingsVariant(settingsVariant: ISettingsVariant): Observable<SettingsVariantDeleteResult> {
    const url = `${environment.apiBase}/settings-variant/${settingsVariant.id}`;
    if (isDevMode()) {
      console.log('[DEV] [MasterSuitesService] deleting settings variant', settingsVariant.id);
    }
    return this.http.delete<SettingsVariantDeleteResult>(url, httpOptions);
  }

  // Enhancement Variants

  createEnhancementVariant(
    data: {
      name: string;
      assets: {
        masterSuiteTouchAssetId: string;
        index: number;
      }[];
    },
    masterSuiteId: string,
  ) {
    const url = `${environment.apiBase}/master-suite/${masterSuiteId}/enhancement-variants`;
    return this.http.post<EnhancementVariantCreate>(url, data);
  }
  updateEnhancementVariant(
    data: {
      name: string;
      default: boolean;
      assets: {
        masterSuiteTouchAssetId: string;
        index: number;
      }[];
    },
    enhancementVariantId: string,
  ) {
    const url = `${environment.apiBase}/enhancement-variants/${enhancementVariantId}`;
    return this.http.put<any>(url, data);
  }

  getEnhancementVariantOfMasterSuite(params: {
    MasterSuiteId: string;
    Name?: string;
    PageSize?: number;
    PageNumber?: number;
  }) {
    const url = `${environment.apiBase}/enhancement-variants/filter`;
    return this.http.get<EnhancementVariantPaging>(url, { params: params });
  }

  deleteEnhancementVariantById(enhancementVariantId: string) {
    const url = `${environment.apiBase}/enhancement-variants/${enhancementVariantId}`;
    return this.http.delete<any>(url);
  }

  getEnhancementVariantById(enhancementVariantId: string) {
    const url = `${environment.apiBase}/enhancement-variants/${enhancementVariantId}`;
    return this.http.get<EnhancementVariantResult>(url);
  }

  updateEnhancementVariantAssetsIndex(
    data: {
      updateId: string;
      startId?: string;
      endId?: string;
    },
    enhancementVariantId: string,
  ) {
    const url = `${environment.apiBase}/enhancement-variants/${enhancementVariantId}/enhancement-variant-assets/order`;
    return this.http.post<any>(url, data);
  }

  applyEnhancementVariantForProductsAfterCreated(enhancementVariantId: string) {
    const url = `${environment.apiBase}/master-suite/actions/apply-enhancement-variant-after-created/${enhancementVariantId}`;
    return this.http.patch<EnhancementVariantCreate>(url, null);
  }

  getAllEnhancementVariantOfMasterSuite(masterSuiteId: string) {
    const url = `${environment.apiBase}/master-suite/${masterSuiteId}/enhancement-variants`;
    return this.http.get<EnhancementVariantListResult>(url);
  }
}
